/**
 * <mta-tabs> — accessible tab switcher for sections/mta-product-tabs.liquid.
 * All panels are rendered server-side; this only toggles `hidden` and
 * `aria-selected`, and supports arrow-key / Home / End navigation
 * (WAI-ARIA Tabs pattern with automatic activation).
 */
class MtaTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = this.tabs.map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls') || '')}`));

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);

    // Theme editor: when a tab block is selected, show it.
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /** @param {number} index */
  select(index, focus = false) {
    if (!this.tabs.length) return;
    const i = ((index % this.tabs.length) + this.tabs.length) % this.tabs.length;

    this.tabs.forEach((tab, n) => {
      const active = n === i;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
      const panel = this.panels[n];
      if (panel) panel.hidden = !active;
    });

    if (focus) this.tabs[i].focus();
  }

  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!tab || !this.contains(tab)) return;
    this.select(this.tabs.indexOf(tab));
  };

  #onKeydown = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!tab) return;
    const current = this.tabs.indexOf(tab);
    if (current < 0) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
        next = current + 1;
        break;
      case 'ArrowLeft':
        next = current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, true);
  };

  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    const tab = target.closest('[role="tab"]');
    if (tab) this.select(this.tabs.indexOf(tab));
  };
}

if (!customElements.get('mta-tabs')) {
  customElements.define('mta-tabs', MtaTabs);
}
